import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import { cx } from './cx';
import { TONE_ICON, TONE_LABEL, TONE_STYLE, type Tone } from './tone';

export interface AlertProps {
  tone?: Tone;
  title?: ReactNode;
  children?: ReactNode;
  onDismiss?: () => void;
  dismissLabel?: string;
  /**
   * The alert appeared in response to something (e.g. a failed save): announce it.
   * danger/warning → role="alert" (assertive), info/success → role="status" (polite).
   * Leave false for alerts that are part of the page on load.
   */
  live?: boolean;
  /** Override the spoken tone prefix (i18n). */
  toneLabel?: string;
  className?: string;
}

/** Inline message box (Bootstrap .alert as used by MantisBT's message/error pages). */
export function Alert({
  tone = 'info',
  title,
  children,
  onDismiss,
  dismissLabel = 'Dismiss',
  live = false,
  toneLabel,
  className,
}: AlertProps) {
  const Icon = TONE_ICON[tone];
  const role = live ? (tone === 'danger' || tone === 'warning' ? 'alert' : 'status') : undefined;
  return (
    <div
      role={role}
      className={cx('flex gap-3 border px-4 py-3 text-base', TONE_STYLE[tone], className)}
    >
      <Icon aria-hidden="true" className="mt-0.5 size-5 shrink-0" />
      <div className="min-w-0 flex-1">
        <span className="sr-only">{toneLabel ?? TONE_LABEL[tone]}: </span>
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className={cx(title && 'mt-0.5', 'text-sm')}>{children}</div>}
      </div>
      {onDismiss && (
        <button
          type="button"
          aria-label={dismissLabel}
          onClick={onDismiss}
          className="-me-1 -mt-0.5 flex size-7 shrink-0 items-center justify-center opacity-80 hover:opacity-100"
        >
          <X aria-hidden="true" className="size-4" />
        </button>
      )}
    </div>
  );
}
